import { useEffect, useState } from 'react';
import { accounts } from '../backend';
import { currentWorkspace } from '../workspace';
import { useApp } from '../store';
import { CURRENCIES, currencyByCode, asAmount } from '../money';

/**
 * THE WORKSPACE'S BASE CURRENCY AND EXCHANGE RATES — tenant-wide (0117/0118), an
 * admin's to set, read everywhere money is drawn. Shown in the two places that both
 * make sense: the Workspace hub's Settings tab (the workspace's own setup) and the
 * board Settings → Finance & payments tab, beside the money controls (#488). The
 * SAME editor and the SAME store state in both, so there is one source of truth —
 * only the wrapping heading differs. Anyone who is not an admin of this workspace
 * sees the read-only summary; the server enforces the same guard (the 0117/0118
 * column grant + admin-of-own-workspace RLS), so the editor never offers a write
 * the database would refuse.
 */
export default function CurrencyRates() {
  // Am I an admin of the workspace I am in? Read the same way the hub does —
  // `workspaces()` runs as its owner and carries the admin flag, and never goes
  // through my_tenant(), so it is right even when a token is missing its claim.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let stop = false;
    accounts
      .workspaces()
      .then((list) => {
        if (stop) return;
        const here = currentWorkspace() || list[0]?.id || '';
        const mine = list.find((w) => w.id === here) ?? list[0];
        setIsAdmin(!!mine?.isAdmin);
      })
      .catch(() => setIsAdmin(false));
    return () => {
      stop = true;
    };
  }, []);

  // The workspace's base currency (0117) and its exchange rates (0118).
  const currency = useApp((s) => s.currency);
  const setWorkspaceCurrency = useApp((s) => s.setWorkspaceCurrency);
  const [currencyTrouble, setCurrencyTrouble] = useState('');
  const fxRates = useApp((s) => s.fxRates);
  const setWorkspaceRate = useApp((s) => s.setWorkspaceRate);
  const [rateDraft, setRateDraft] = useState<Record<string, string>>({});
  const [newRateCode, setNewRateCode] = useState('');
  const [rateTrouble, setRateTrouble] = useState('');

  if (!isAdmin) {
    return (
      <p className="hint">
        Budgets roll up to {currencyByCode(currency).name} (
        {currencyByCode(currency).symbol.trim()}), the base currency set for the whole workspace by an
        administrator. Payments in other currencies are converted to it.
      </p>
    );
  }

  return (
    <div className="acc-currency">
      <h3>Base Currency</h3>
      <label className="stack">
        Every budget in this workspace rolls up to
        <select
          value={currency}
          onChange={(e) => {
            const code = e.target.value;
            setCurrencyTrouble('');
            void setWorkspaceCurrency(code).catch(() =>
              setCurrencyTrouble(
                'That could not be saved. Only an administrator can change the currency, and only for this workspace.',
              ),
            );
          }}
        >
          {CURRENCIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.symbol.trim()} — {c.name} ({c.code})
            </option>
          ))}
        </select>
      </label>
      {currencyTrouble && (
        <p className="acc-trouble" role="alert">
          {currencyTrouble}
        </p>
      )}
      <p className="hint">
        The base (reporting) currency — every budget total rolls up to it, and it is the default for
        new lines. A payment in another currency is converted to it at a rate you set below. Changing
        the base changes the symbol, not the numbers — no amount is converted.
      </p>

      <div className="acc-fx">
        <h3>Exchange Rates</h3>
        <p className="hint">
          Set the current rate for each other currency you pay in. A committed line uses the current
          rate; a paid line keeps the rate it was paid at, so changing a rate never rewrites what has
          already been paid.
        </p>
        {Object.keys(fxRates).length === 0 && (
          <p className="hint acc-fx-empty">No other currencies yet. Add one below when a project pays in it.</p>
        )}
        {Object.keys(fxRates)
          .sort()
          .map((code) => (
            <div className="acc-fx-row" key={code}>
              <span className="acc-fx-lead">
                1 {code} =
              </span>
              <input
                className="input tnum acc-fx-input"
                inputMode="decimal"
                value={rateDraft[code] ?? String(fxRates[code])}
                onChange={(e) => setRateDraft((d) => ({ ...d, [code]: e.target.value }))}
              />
              <span className="acc-fx-code">{currency}</span>
              <button
                className="btn btn-small"
                onClick={() => {
                  const r = asAmount(rateDraft[code] ?? String(fxRates[code]));
                  setRateTrouble('');
                  if (r == null) { setRateTrouble('Enter a rate greater than zero.'); return; }
                  void setWorkspaceRate(code, r)
                    .then(() => setRateDraft((d) => { const n = { ...d }; delete n[code]; return n; }))
                    .catch(() => setRateTrouble('That could not be saved. Only an administrator can change a rate.'));
                }}
              >
                Save
              </button>
              <button
                className="btn btn-small acc-fx-remove"
                onClick={() => {
                  setRateTrouble('');
                  void setWorkspaceRate(code, null).catch(() =>
                    setRateTrouble('That could not be saved. Only an administrator can change a rate.'),
                  );
                }}
              >
                Remove
              </button>
            </div>
          ))}
        <div className="acc-fx-add">
          <select className="input" value={newRateCode} onChange={(e) => setNewRateCode(e.target.value)}>
            <option value="">Add a currency…</option>
            {CURRENCIES.filter((c) => c.code !== currency && fxRates[c.code] == null).map((c) => (
              <option key={c.code} value={c.code}>
                {c.symbol.trim()} — {c.name} ({c.code})
              </option>
            ))}
          </select>
          {newRateCode && (
            <>
              <span className="acc-fx-lead">
                1 {newRateCode} =
              </span>
              <input
                className="input tnum acc-fx-input"
                inputMode="decimal"
                placeholder="e.g. 6.78"
                value={rateDraft[newRateCode] ?? ''}
                onChange={(e) => setRateDraft((d) => ({ ...d, [newRateCode]: e.target.value }))}
              />
              <span className="acc-fx-code">{currency}</span>
              <button
                className="btn btn-small btn-primary"
                onClick={() => {
                  const r = asAmount(rateDraft[newRateCode] ?? '');
                  setRateTrouble('');
                  if (r == null) { setRateTrouble('Enter a rate greater than zero.'); return; }
                  const code = newRateCode;
                  void setWorkspaceRate(code, r)
                    .then(() => { setNewRateCode(''); setRateDraft((d) => { const n = { ...d }; delete n[code]; return n; }); })
                    .catch(() => setRateTrouble('That could not be saved. Only an administrator can change a rate.'));
                }}
              >
                Add
              </button>
            </>
          )}
        </div>
        {rateTrouble && (
          <p className="acc-trouble" role="alert">{rateTrouble}</p>
        )}
      </div>
    </div>
  );
}
